import { useEffect, useState } from 'react';
import { ChevronIcon } from '../../../bombiix-icon';
import type { BombiixCellRendererParams } from '../types';

export interface ExpandChevronCellProps extends Partial<BombiixCellRendererParams> {
  /** Controlled mode: current expanded state. Ignored unless `onToggle` is also supplied. */
  expanded?: boolean;
  /** Controlled mode: supplying this makes the caller own the state; the AG Grid node is not touched. */
  onToggle?: () => void;
  expandLabel?: string;
  collapseLabel?: string;
  /** Extra class on the button — pass `cag-chevron-toggle` for the 12px header/group-sized glyph. */
  className?: string;
}

/**
 * Expand/collapse chevron used for both master/detail rows and tree-data group
 * rows. Renders the master-design ChevronIcon (TSX, currentColor) inside
 * `.bombiix-icon-container` so the affordance matches the Core Product / Detailed
 * Costing look. ChevronIcon points down (collapsed); the `--expanded` class rotates
 * it 180° to point up. Hidden (but space-preserving) on rows with no children so
 * columns stay aligned.
 */
function ExpandChevronCell(params: ExpandChevronCellProps) {
  const { node, expanded: expandedProp, onToggle, expandLabel = 'Expand row', collapseLabel = 'Collapse row', className } = params;
  const controlled = typeof onToggle === 'function';
  const [nodeExpanded, setNodeExpanded] = useState<boolean>(Boolean(node?.expanded));

  useEffect(() => {
    if (controlled || !node) return undefined;
    const onExpanded = () => setNodeExpanded(Boolean(node.expanded));
    node.addEventListener('expandedChanged', onExpanded);
    return () => node.removeEventListener('expandedChanged', onExpanded);
  }, [controlled, node]);

  if (!controlled) {
    const isExpandable = Boolean(node?.master) || Boolean(node?.group);
    if (!isExpandable) {
      return <span className="cag-chevron cag-chevron--hidden" aria-hidden="true" />;
    }
  }

  const expanded = controlled ? Boolean(expandedProp) : nodeExpanded;
  const label = expanded ? collapseLabel : expandLabel;

  return (
    <button
      type="button"
      className={`bombiix-icon-container${className ? ` ${className}` : ''}`}
      onClick={() => (controlled ? onToggle?.() : node?.setExpanded(!node.expanded))}
      aria-label={label}
      aria-expanded={expanded}
      title={label}
    >
      <ChevronIcon size={20} className={`cag-chevron-icon${expanded ? ' cag-chevron-icon--expanded' : ''}`} />
    </button>
  );
}

export default ExpandChevronCell;
